/**
 * ENVIAR CON DESHACER (pedido de los usuarios vía Manuel, 2026-10-08): "cuando se envíe un email,
 * un delay de unos 10 segundos y una ventanita a la izquierda con un mensajito: si te faltó algo,
 * le das undo y no se envía. Si cambias de pantalla y te vas a otro lado, ahí sí ya lo envía".
 *
 * Es una cola en el navegador, no en el servidor: el email NO sale hasta que pasan los segundos.
 * Cada pantalla que manda un correo encola `{ ejecutar, deshacer }`: `ejecutar` hace la llamada real
 * (recibe `keepalive` cuando la pestaña se está cerrando) y `deshacer` devuelve el borrador al
 * cuadro. El host (`UndoSendHost`) pinta la ventanita con la cuenta regresiva; `AppShell` vacía la
 * cola al cambiar de ruta y al cerrar la pestaña (`pagehide`), para que nada quede sin enviar.
 */
import type { ReqOpts } from './api';

export type EnvioPendiente = {
  id: string;
  /** Lo que ve la persona: "Email to claims@carrier.com". */
  etiqueta: string;
  /** Segundos que faltan (lo lleva el host; empieza en `segundos`). */
  restante: number;
  estado: 'esperando' | 'enviando' | 'enviado' | 'fallo';
  error?: string | null;
};

type Entrada = EnvioPendiente & {
  ejecutar: (opts: ReqOpts) => Promise<void>;
  deshacer?: () => void;
  alTerminar?: (ok: boolean) => void;
  timer: number | null;
};

export const SEGUNDOS_PARA_DESHACER = 10;
const cola = new Map<string, Entrada>();
const oyentes = new Set<() => void>();
let tic: number | null = null;

function avisar() { for (const fn of oyentes) fn(); }
export function suscribir(fn: () => void): () => void { oyentes.add(fn); return () => { oyentes.delete(fn); }; }
export function pendientes(): EnvioPendiente[] {
  // Se quitan las funciones y el timer: lo que queda es lo que la ventanita pinta. (El lint no acepta el
  // prefijo `_` para descartar: tumbó el CI de main desde el 2026-10-08e.)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return [...cola.values()].map(({ ejecutar: _e, deshacer: _d, alTerminar: _t, timer: _ti, ...p }) => p);
}

function arrancarTic() {
  if (tic != null) return;
  tic = window.setInterval(() => {
    let hayEsperando = false;
    for (const e of cola.values()) {
      if (e.estado !== 'esperando') continue;
      hayEsperando = true;
      e.restante = Math.max(0, e.restante - 1);
      if (e.restante === 0) void ejecutarAhora(e.id, {});
    }
    if (!hayEsperando && tic != null) { window.clearInterval(tic); tic = null; }
    avisar();
  }, 1000);
}

async function ejecutarAhora(id: string, opts: ReqOpts): Promise<void> {
  const e = cola.get(id);
  if (!e || e.estado !== 'esperando') return;
  e.estado = 'enviando'; e.restante = 0; avisar();
  try {
    await e.ejecutar(opts);
    e.estado = 'enviado'; avisar();
    e.alTerminar?.(true);
    window.setTimeout(() => { cola.delete(id); avisar(); }, 2500);
  } catch (err) {
    // El fallo se queda en la ventanita hasta que lo cierren: la pantalla que lo mandó puede ya no existir.
    e.estado = 'fallo'; e.error = err instanceof Error ? err.message : 'Error'; avisar();
    e.alTerminar?.(false);
  }
}

/** Encola un envío. Devuelve el id; la ventanita aparece sola. */
export function encolarEnvio(p: {
  etiqueta: string;
  ejecutar: (opts: ReqOpts) => Promise<void>;
  deshacer?: () => void;
  alTerminar?: (ok: boolean) => void;
  segundos?: number;
}): string {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  cola.set(id, { id, etiqueta: p.etiqueta, restante: p.segundos ?? SEGUNDOS_PARA_DESHACER, estado: 'esperando', ejecutar: p.ejecutar, deshacer: p.deshacer, alTerminar: p.alTerminar, timer: null });
  arrancarTic(); avisar();
  return id;
}

/** El botón Undo: saca el envío de la cola y devuelve el borrador. */
export function deshacerEnvio(id: string): boolean {
  const e = cola.get(id);
  if (!e || e.estado !== 'esperando') return false;
  cola.delete(id);
  try { e.deshacer?.(); } catch { /* el borrador no volvió: el correo igual no salió */ }
  avisar();
  return true;
}

export function descartarAviso(id: string): void { cola.delete(id); avisar(); }

/** Cambio de pantalla o cierre de la pestaña: todo lo que espera sale YA (con keepalive al cerrar). */
export function vaciarCola(motivo: 'navegacion' | 'cierre'): void {
  for (const e of [...cola.values()]) {
    if (e.estado === 'esperando') void ejecutarAhora(e.id, motivo === 'cierre' ? { keepalive: true } : {});
  }
}

export function hayEnviosEsperando(): boolean { return [...cola.values()].some((e) => e.estado === 'esperando'); }
